import { getItem } from '../data/index.js';
import { GameError } from '../errors.js';
import type { GameEvent } from '../events.js';
import type { BotUser, TeamSlot } from '../state.js';
import type { ItemType } from '../types.js';
import { itemPower, powerItem, teamPower } from '../formulas/power.js';
import { unlockedTeamSlots } from './levels.js';

export function emptySlot(): TeamSlot {
  return { characters: 0, weapons: 0, shields: 0, pets: 0 };
}

/**
 * `rpg/team.ts#canEquipOnSlot`: characters always fit; other items need the
 * slot's character to share their element (or either side to be Multi).
 */
export function canEquipOnSlot(save: BotUser, slot: number, type: ItemType, id: number): boolean {
  const item = getItem(type, id);
  const owned = save.summoned[type].owned.find((o) => o.id === id);
  if (!item || !owned || owned.total < 1) return false;
  if (type === 'characters') return true;
  const character = getItem('characters', save.summoned.team.equiped[slot]?.characters ?? 0);
  return !!character && (item.element === character.element || item.element === 'Multi' || character.element === 'Multi');
}

/**
 * `rpg/team.ts#toggleEquipFromInventory` + `equipItem`: equipping the item
 * already in the slot unequips it; an item lives in one slot at most; a new
 * character drops gear whose element differs from its own (`equipItem`
 * compares elements strictly: Multi gear goes too unless the character is
 * Multi). Every change re-stores the team power (`attack`).
 */
export function toggleEquip(save: BotUser, slot: number, type: ItemType, id: number, events: GameEvent[]): void {
  if (!Number.isInteger(slot) || slot < 0 || slot >= unlockedTeamSlots(save.level.level)) {
    throw new GameError('slotLocked', { slot });
  }
  const team = save.summoned.team.equiped;
  while (team.length <= slot) team.push(emptySlot());
  const target = team[slot] as TeamSlot;

  if (target[type] === id && id !== 0) {
    target[type] = 0;
    save.attack = teamPower(save);
    events.push({ type: 'itemUnequipped', slot, itemType: type, id });
    return;
  }

  const item = getItem(type, id);
  if (!item) throw new GameError('unknownItem');
  const owned = save.summoned[type].owned.find((o) => o.id === id);
  if (!owned || owned.total < 1) throw new GameError('notOwned');
  if (type !== 'characters') {
    if (!target.characters) throw new GameError('noCharacterInSlot');
    if (!canEquipOnSlot(save, slot, type, id)) throw new GameError('elementMismatch');
  }

  for (const other of team) if (other[type] === id) other[type] = 0;

  if (type === 'characters') {
    for (const gear of ['weapons', 'shields', 'pets'] as const) {
      const g = getItem(gear, target[gear]);
      if (g && g.element !== item.element) target[gear] = 0;
    }
  }
  target[type] = id;
  save.attack = teamPower(save);
  events.push({ type: 'itemEquipped', slot, itemType: type, id });
}

/**
 * Web addition: the strongest legal team from owned items — per unlocked
 * slot, the best free character, then the best free matching gear.
 */
export function bestTeam(save: BotUser): TeamSlot[] {
  const slots = unlockedTeamSlots(save.level.level);
  const used: Record<ItemType, Set<number>> = { characters: new Set(), weapons: new Set(), shields: new Set(), pets: new Set() };
  const ranked = (type: ItemType) =>
    save.summoned[type].owned
      .filter((o) => o.total > 0)
      .map((o) => ({ id: o.id, power: itemPower(powerItem(save, type, o.id), save) }))
      .sort((a, b) => b.power - a.power || a.id - b.id);
  const pools = { characters: ranked('characters'), weapons: ranked('weapons'), shields: ranked('shields'), pets: ranked('pets') };
  const team: TeamSlot[] = [];
  for (let i = 0; i < slots; i++) {
    const character = pools.characters.find((c) => !used.characters.has(c.id));
    if (!character) break;
    used.characters.add(character.id);
    const charElement = getItem('characters', character.id)?.element;
    const slot = emptySlot();
    slot.characters = character.id;
    for (const gear of ['weapons', 'shields', 'pets'] as const) {
      const pick = pools[gear].find((g) => {
        if (used[gear].has(g.id)) return false;
        const el = getItem(gear, g.id)?.element;
        return el === charElement || el === 'Multi' || charElement === 'Multi';
      });
      if (pick) {
        used[gear].add(pick.id);
        slot[gear] = pick.id;
      }
    }
    team.push(slot);
  }
  return team;
}

export function autoEquip(save: BotUser, events: GameEvent[]): void {
  const team = bestTeam(save);
  team.forEach((slot, i) => {
    for (const type of ['characters', 'weapons', 'shields', 'pets'] as const) {
      if (slot[type] && save.summoned.team.equiped[i]?.[type] !== slot[type]) {
        events.push({ type: 'itemEquipped', slot: i, itemType: type, id: slot[type] });
      }
    }
  });
  save.summoned.team.equiped = team;
  save.attack = teamPower(save);
}
